import { useEffect, useMemo, useState } from "react";
import { getBySlug } from "../../../server/Category";
import {
  buildCategoryPeerImages,
  extractProductsForSlugNode,
  filterProductsByCategoryLevels,
  findCategoryNodeBySlug,
  getPeerCategoryLevels,
  getPeerCategorySlug,
  resolveBranchId,
} from "../../../utils/categoryPeerImages";

const loadCategoryProducts = async (branch, categoryLevels) => {
  const { department, subgroup, family, group } = categoryLevels;
  const targetSlug = family || subgroup || group || department;

  if (!targetSlug) {
    return { categoryName: "", products: [] };
  }

  let categoryRes = null;
  let fallbackRes = null;
  let products = [];

  // מקבץ: fetch family directly (same as FamilyChangeDialog).
  if (family) {
    categoryRes = await getBySlug(branch, family, false, true);
    products = Array.isArray(categoryRes?.products)
      ? categoryRes.products
      : extractProductsForSlugNode(categoryRes, family);
  }

  if (!products.length) {
    const fetchSlug = family
      ? subgroup || department || targetSlug
      : department || targetSlug;

    categoryRes = await getBySlug(branch, fetchSlug, false, true);
    products = extractProductsForSlugNode(categoryRes, targetSlug);

    if (!products.length && fetchSlug !== targetSlug) {
      fallbackRes = await getBySlug(branch, targetSlug, false, true);
      products = Array.isArray(fallbackRes?.products)
        ? fallbackRes.products
        : extractProductsForSlugNode(fallbackRes, targetSlug);
    }
  }

  products = filterProductsByCategoryLevels(products, categoryLevels);

  const targetNode =
    findCategoryNodeBySlug(categoryRes, targetSlug) ??
    (fallbackRes ? findCategoryNodeBySlug(fallbackRes, targetSlug) : null);

  return {
    categoryName:
      targetNode?.name || categoryRes?.name || fallbackRes?.name || "",
    products,
  };
};

const useCategoryPeerProducts = ({
  stationId,
  productId,
  productCategories,
}) => {
  const [peers, setPeers] = useState([]);
  const [categoryName, setCategoryName] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);

  const categoryLevels = useMemo(
    () => getPeerCategoryLevels(productCategories),
    [productCategories],
  );
  const categorySlug = getPeerCategorySlug(productCategories);
  const branch = resolveBranchId(stationId);

  const levelsKey = useMemo(
    () =>
      [
        categoryLevels.department,
        categoryLevels.group,
        categoryLevels.subgroup,
        categoryLevels.family,
      ].join("|"),
    [categoryLevels],
  );

  useEffect(() => {
    if (branch == null || !productId || !categorySlug) {
      setPeers([]);
      setCategoryName("");
      setLoading(false);
      setLoadFailed(false);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    setLoadFailed(false);

    loadCategoryProducts(branch, categoryLevels)
      .then(({ categoryName: name, products }) => {
        if (cancelled) return;
        setCategoryName(name);
        setPeers(buildCategoryPeerImages(products, productId));
      })
      .catch(() => {
        if (cancelled) return;
        setPeers([]);
        setCategoryName("");
        setLoadFailed(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [branch, productId, categorySlug, levelsKey, categoryLevels]);

  return {
    peers,
    categoryName,
    categorySlug,
    loading,
    loadFailed,
  };
};

export default useCategoryPeerProducts;
